// US-076 — Pending-action card + outcome reporter.
//
// Mirrors `ls-inbox.jsx:548-743`. Magenta-tinted card visible when the
// conv has a pending action; expands to an outcome form (radios:
// `À relancer`, `Archiver`, `Lead qualifié`) on CTA click. Confirmation
// writes the outcome via `useOutcomeReporter` (or `usePushToLeads` for
// `client`).
//
// `À relancer` is disabled when `relancesEnabled === false` for the
// owning account; tooltip explains how to enable it.

import { useState } from 'react';

import { useT } from '@barel/i18n';
import { Button, Card } from '@barel/ui';

import type { ConversationView } from './types';

export interface PendingActionCardProps {
  conversation: Pick<ConversationView, 'id' | 'pendingAction' | 'relancesEnabled'>;
  /** Called when the user submits the outcome form. */
  onSubmit: (
    status: 'followup' | 'archive' | 'client' | 'won' | 'lost' | 'unknown',
  ) => Promise<void> | void;
  /** True while the API call is in flight. */
  submitting?: boolean;
}

// Wave D5 (US-076) — outcome enum extended with the three terminal /
// indeterminate states the user asked for: `won` (deal closed), `lost`
// (deal dead), `unknown` (status not yet known — keep in queue).
type OutcomeChoice = 'followup' | 'archive' | 'client' | 'won' | 'lost' | 'unknown';

const OUTCOMES: { id: OutcomeChoice; icon: string }[] = [
  { id: 'followup', icon: '🔄' },
  { id: 'client', icon: '⭐' },
  { id: 'won', icon: '🏆' },
  { id: 'lost', icon: '💔' },
  { id: 'unknown', icon: '❔' },
  { id: 'archive', icon: '📁' },
];

export function PendingActionCard({
  conversation,
  onSubmit,
  submitting = false,
}: PendingActionCardProps) {
  const t = useT('inbox');
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<OutcomeChoice>('followup');
  const action = conversation.pendingAction;
  if (!action) return null;
  const relancesEnabled = conversation.relancesEnabled !== false;
  const safeStatus: OutcomeChoice = status === 'followup' && !relancesEnabled ? 'archive' : status;

  return (
    <Card
      padding="md"
      radius="xl"
      data-ls-region="pending-action-card"
      style={{
        background: 'var(--magenta-bg)',
        borderColor: 'var(--magenta-border)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
          <div
            aria-hidden
            style={{
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: 10,
              background: 'var(--magenta-bg)',
              border: '1px solid var(--magenta-border)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            {action.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                flexWrap: 'wrap',
                marginBottom: 'var(--space-1)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--text-2xs)',
                  fontWeight: 800,
                  color: 'var(--magenta)',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                {t('thread.pending.kicker')}
              </span>
              {action.since ? (
                <span
                  style={{
                    fontSize: 'var(--text-2xs)',
                    color: 'var(--text-tertiary)',
                    padding: '1px 7px',
                    borderRadius: 10,
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  {t('thread.pending.since', { value: action.since as string })}
                </span>
              ) : null}
            </div>
            <div
              style={{
                fontSize: 'var(--text-md)',
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: 'var(--space-1_5)',
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <span aria-hidden>{action.icon}</span>
              <span>{action.label}</span>
            </div>
            {action.detail ? (
              <div
                style={{
                  fontSize: 'var(--text-2xs)',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--text-secondary)',
                  background: 'var(--bg-base)',
                  border: '1px solid var(--magenta-border)',
                  padding: '6px 10px',
                  borderRadius: 6,
                  marginBottom: 'var(--space-1_5)',
                  display: 'inline-block',
                }}
              >
                {action.detail}
              </div>
            ) : null}
            {action.reason ? (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                {action.reason}
              </div>
            ) : null}
          </div>
        </div>

        <div
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--text-tertiary)',
            padding: '8px 11px',
            borderRadius: 7,
            background: 'var(--bg-base)',
            border: '1px dashed var(--border-strong)',
          }}
        >
          <strong style={{ color: 'var(--text-secondary)' }}>
            {t('thread.pending.pauseExplanationStrong')}
          </strong>{' '}
          {t('thread.pending.pauseExplanation')}
        </div>

        {!open ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-2)',
            }}
          >
            <Button
              variant="primary"
              size="sm"
              onClick={() => setOpen(true)}
              data-ls-action="open-outcome"
            >
              {t('thread.pending.cta')}
            </Button>
          </div>
        ) : (
          <div
            data-ls-region="outcome-form"
            style={{
              padding: 'var(--space-3)',
              borderRadius: 9,
              background: 'var(--bg-base)',
              border: '1px solid var(--border-strong)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2_5)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--text-2xs)',
                fontWeight: 700,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {t('thread.pending.formTitle')}
            </div>
            <div
              role="radiogroup"
              aria-label={t('thread.pending.formTitle')}
              style={{
                display: 'flex',
                gap: 'var(--space-1_5)',
                flexWrap: 'wrap',
              }}
            >
              {OUTCOMES.map((o) => {
                const isFollowup = o.id === 'followup';
                const disabled = isFollowup && !relancesEnabled;
                const active = safeStatus === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={disabled}
                    title={
                      disabled
                        ? t('thread.pending.relancesDisabledTooltip')
                        : labelForOutcome(t, o.id)
                    }
                    onClick={() => !disabled && setStatus(o.id)}
                    className="ls-focus-ring"
                    data-ls-outcome={o.id}
                    style={{
                      flex: '1 1 140px',
                      padding: '9px 11px',
                      borderRadius: 8,
                      background: active ? 'var(--accent-dim)' : 'var(--bg-elevated)',
                      border: `1px solid ${active ? 'var(--accent-border)' : 'var(--border-strong)'}`,
                      color: active ? 'var(--accent)' : 'var(--text-primary)',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.45 : 1,
                      fontFamily: 'var(--font-sans)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: 16 }} aria-hidden>
                      {o.icon}
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-1_5)',
                        }}
                      >
                        {labelForOutcome(t, o.id)}
                        {disabled ? (
                          <span
                            style={{
                              fontSize: 'var(--text-2xs)',
                              fontWeight: 600,
                              color: 'var(--text-tertiary)',
                              background: 'var(--bg-base)',
                              border: '1px solid var(--border)',
                              borderRadius: 3,
                              padding: '1px 5px',
                            }}
                          >
                            {t('thread.pending.relancesOff')}
                          </span>
                        ) : null}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--text-2xs)',
                          color: 'var(--text-tertiary)',
                          fontWeight: 500,
                        }}
                      >
                        {disabled ? t('thread.pending.relancesOffHint') : hintForOutcome(t, o.id)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--space-2)',
              }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={submitting}
              >
                {t('thread.pending.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={submitting}
                onClick={async () => {
                  await onSubmit(safeStatus);
                }}
                data-ls-action="confirm-outcome"
              >
                {t('thread.pending.confirm')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function labelForOutcome(t: (key: string) => string, id: OutcomeChoice): string {
  switch (id) {
    case 'followup':
      return t('thread.pending.outcomeFollowupLabel');
    case 'archive':
      return t('thread.pending.outcomeArchiveLabel');
    case 'client':
      return t('thread.pending.outcomeClientLabel');
    case 'won':
      return t('thread.pending.outcomeWonLabel');
    case 'lost':
      return t('thread.pending.outcomeLostLabel');
    case 'unknown':
      return t('thread.pending.outcomeUnknownLabel');
  }
}

function hintForOutcome(t: (key: string) => string, id: OutcomeChoice): string {
  switch (id) {
    case 'followup':
      return t('thread.pending.outcomeFollowupHint');
    case 'archive':
      return t('thread.pending.outcomeArchiveHint');
    case 'client':
      return t('thread.pending.outcomeClientHint');
    case 'won':
      return t('thread.pending.outcomeWonHint');
    case 'lost':
      return t('thread.pending.outcomeLostHint');
    case 'unknown':
      return t('thread.pending.outcomeUnknownHint');
  }
}
